import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

// Guard client-side para no disparar track-first-login multiple veces:
// React StrictMode ejecuta efectos 2x, y onAuthStateChange + getSession
// ambos llaman cargarEjecutivo. Sin guard llegaban 3+ requests concurrentes.
// El backend ya tiene UPDATE atómico con RETURNING (sprint 15.29), pero
// reducir las requests innecesarias ahorra round-trips y costo.
const TRACK_GUARD_KEY = 'vera_first_login_tracked';

export function AuthProvider({ children }) {
  const [user,      setUser]      = useState(null);
  const [ejecutivo, setEjecutivo] = useState(null);
  const [loading,   setLoading]   = useState(true);
  // Ref para que la guard sobreviva al mount-unmount-mount de StrictMode
  // dentro de la misma instancia del Provider.
  const trackedRef = useRef(false);

  useEffect(() => {
    // Sesión inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) cargarEjecutivo(session.user.id);
      else setLoading(false);
    });

    // Escuchar cambios de sesión
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) cargarEjecutivo(session.user.id);
        else { setEjecutivo(null); setLoading(false); }
      }
    );
    return () => subscription.unsubscribe();
  }, []);

  async function cargarEjecutivo(userId) {
    const { data } = await supabase
      .from('ejecutivos')
      .select('*, inmobiliaria:inmobiliarias(nombre_empresa, estado_cuenta, fecha_inicio, created_at)')
      .eq('id', userId)
      .single();
    setEjecutivo(data);
    setLoading(false);

    // Aviso interno a Pablo del primer login. Fire-and-forget. Doble guard:
    // 1) Ref para una vez por mount del Provider.
    // 2) sessionStorage por userId para no re-disparar en navegaciones SPA
    //    aunque el Provider se remontara (no debería pero por las dudas).
    // 3) Backend usa UPDATE atómico WHERE primer_login_at IS NULL RETURNING
    //    como verdad de fuente final.
    if (trackedRef.current) return;
    try {
      const guardKey = `${TRACK_GUARD_KEY}_${userId}`;
      if (sessionStorage.getItem(guardKey) === '1') {
        trackedRef.current = true;
        return;
      }
      trackedRef.current = true;
      sessionStorage.setItem(guardKey, '1');

      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (token) {
        fetch('/api/auth', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type':  'application/json',
          },
          body: JSON.stringify({ op: 'track-first-login' }),
        }).catch(() => {});
      }
    } catch (_) { /* silencioso */ }
  }

  async function logout() {
    await supabase.auth.signOut();
  }

  const isAdmin           = ejecutivo?.rol === 'admin';
  const isSupervisor      = ejecutivo?.rol === 'supervisor';
  const isMaestro         = ejecutivo?.rol === 'maestro';
  const isGerencia        = ejecutivo?.rol === 'gerencia';
  const isSuperadmin      = ejecutivo?.rol === 'superadmin';
  // Sprint 16.1 — nuevo rol: administrador de condominio (Ley 21.442)
  const isAdminCondominio = ejecutivo?.rol === 'admin_condominio';

  return (
    <AuthContext.Provider value={{
      user, ejecutivo, loading, logout,
      isAdmin, isSupervisor, isMaestro, isGerencia, isSuperadmin, isAdminCondominio,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
